'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { getSession, useSession } from 'next-auth/react';
type TUseAccessToken = {
  getAccessToken: () => Promise<Maybe<string>>;
  refreshAndGetAccessToken: () => Promise<Maybe<string>>;
};
let sessionRequest: ReturnType<typeof getSession> | null = null;
function getSessionOnce(): ReturnType<typeof getSession> {
  if (sessionRequest === null) {
    sessionRequest = getSession().finally(() => {
      sessionRequest = null;
    });
  }
  return sessionRequest;
}

/**
 * Hook that exposes access token from session.
 *
 * `getAccessToken` reads from a ref so it always returns the latest token,
 * even when called from stale closures (e.g. React Query retries).
 *
 * `refreshAndGetAccessToken` forces server-side refresh and returns fresh token directly.
 */
export function useAccessToken() {
  const $ = _c(12);
  const {
    data: session,
    status,
    update
  } = useSession();
  let t0;
  if ($[0] !== session || $[1] !== status) {
    t0 = {
      session,
      status
    };
    $[0] = session;
    $[1] = status;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const sessionRef = useRef(t0);
  let t1;
  let t2;
  if ($[3] !== session || $[4] !== status) {
    t1 = () => {
      sessionRef.current = {
        session,
        status
      };
    };
    t2 = [session, status];
    $[3] = session;
    $[4] = status;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async () => {
      const current = sessionRef.current;
      if (current.status !== "loading") {
        return current.session?.accessToken ?? null;
      }
      const fresh = await getSessionOnce();
      return fresh?.accessToken ?? null;
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getAccessToken = t3;
  let t4;
  if ($[8] !== update) {
    t4 = async () => {
      const fresh_0 = await update();
      sessionRef.current = {
        session: fresh_0,
        status: fresh_0 ? "authenticated" : "unauthenticated"
      };
      return fresh_0?.accessToken ?? null;
    };
    $[8] = update;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const refreshAndGetAccessToken = t4;
  let t5;
  if ($[10] !== refreshAndGetAccessToken) {
    t5 = {
      getAccessToken,
      refreshAndGetAccessToken
    };
    $[10] = refreshAndGetAccessToken;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}